import React, { useCallback } from 'react';

import { Checkbox } from '@primer/react';

import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList } from 'react-window';

import Label from '../../../common/Labels/Label';
import LinkButton from '../../../common/Labels/LinkButton';

import { formatDateWithTimeSeparator } from '../../../../utils/date_utils';
import { getThemeColor } from '../../../../utils/theme-utils';

import TooltipWrapper from '../../../common/TooltipWrapper/TooltipWrapper';
import NoVersionsSlate from './NoVersionsSlate';

const VersionRow = ({
  index,
  style,
  data,
}) => {
  const {
    values,
    onItemSelected,
    isValueSelected,
  } = data;

  const row = values[index];
  const version = row?.pipeline_version?.version
  const isSelected = isValueSelected(version);

  const onClick = useCallback((e) => {
    e.stopPropagation();
    if (!row?.deployed) {
      onItemSelected(version);
    }
  }, [row?.deployed, onItemSelected, version]);

  return (
    <div
      id={`pipeline-version-entry-${index}`}
      onClick={onClick}
      style={style}
      className="my-1 mr-2 p-1 flex cursor-pointer flex justify-between items-center bg-white hover:bg-border-light">
      <TooltipWrapper label="Unable to delete a deployed pipeline version" showTooltip={row?.deployed} tooltipProps={{ sx: { display: 'contents' } }}>
        <Checkbox
          id={`selection-checkbox-${version}`}
          checked={isSelected}
          onChange={() => onItemSelected(version)}
          disabled={row.deployed}
        />
        <Label
          className="ml-4 grow"
          style={{ color: row.deployed ? getThemeColor('info') : getThemeColor('brand') }}
        >
          {version}
        </Label>
        <Label
          style={{ color: row.deployed ? getThemeColor('info') : getThemeColor('brand') }}
        >
          {formatDateWithTimeSeparator(row.pipeline_version.created_at, ' at ', '--')}
        </Label>
      </TooltipWrapper>
    </div>
  )
}

// assumes entries in the values array have a `version` and a `created_at` prop
export const SelectablePipelineHistoryList = ({
  listRef,
  values,
  selectedValues = [],
  setSelectedValues = () => {},
  height = 480,
}) => {
  const onSelectAll = () => setSelectedValues(values.filter(({ deployed }) => !deployed).map(({ pipeline_version: { version } }) => version));

  const onDeselectAll = () => setSelectedValues([]);

  const isValueSelected = selectedVersion => !!selectedValues.find(version => version === selectedVersion)

  const onItemSelected = (selectedVersion) => {
    const isSelected = isValueSelected(selectedVersion);
    if (isSelected) {
      setSelectedValues(selectedValues.filter(version => version !== selectedVersion))
    } else {
      setSelectedValues([
        ...selectedValues,
        selectedVersion,
      ])
    }
  }
  return (
    <div className="m-1">
      <div className="mx-1 my-1 flex">
        <LinkButton
          className="mr-4"
          size="sm"
          color="link-blue"
          hoverColor="faded-brand"
          activeColor="highlighted"
          onClick={onSelectAll}
          showUnderlineOnHover
        >
          Select All
        </LinkButton>
        <LinkButton
          size="sm"
          color="link-red"
          hoverColor="faded-error"
          activeColor="error"
          onClick={onDeselectAll}
          showUnderlineOnHover
        >
          Deselect All
        </LinkButton>
      </div>
      <div className="full">
        {
          values?.length > 0 ? (
            <AutoSizer disableHeight>
              {({ width }) => (
                <FixedSizeList
                  ref={listRef}
                  height={height}
                  itemCount={values.length}
                  itemSize={32}
                  width={width}
                  itemData={{
                    values,
                    isValueSelected,
                    onItemSelected,
                  }}
                >
                  {VersionRow}
                </FixedSizeList>
              )}
            </AutoSizer>
          ) : (
            <div className="mt-8">
              <NoVersionsSlate />
            </div>
          )
        }
      </div>
    </div>
  )
}

export default SelectablePipelineHistoryList;
